import { groupBy } from "lodash";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { GroupTitleTemplate, PreviewDrawerLineStyled } from "@ryde/controls";
import { IGenericUploadPrepResult } from "@ryde/shared";
import { blueMui, rydeColors } from "@ryde/theme";

import { DrawerFormWrapperStyled } from "../drawer";
import {
  GenericUploadColumnConflicts,
  GenericUploadPhoneConflicts,
  GenericUploadRowConflicts,
} from "./components";
import type {
  IGenericUploadPhoneConflict,
  IGenericUploadPrepareStatistics,
} from "./generic-upload.models";

interface IGenericUploadPreviewProps {
  statistics: IGenericUploadPrepareStatistics;
  uploadResult?: IGenericUploadPrepResult;
  phoneConflicts: IGenericUploadPhoneConflict[];
  skippedDataIndexes: number[];
  unresolvedPhoneConflictRowsCount: number;
  isExistingPhoneCheckFailed: boolean;
  onToggleSkippedRow: (dataIndex: number) => void;
}

export const GenericUploadPreview: FC<IGenericUploadPreviewProps> = ({
  statistics,
  uploadResult,
  phoneConflicts,
  skippedDataIndexes,
  unresolvedPhoneConflictRowsCount,
  isExistingPhoneCheckFailed,
  onToggleSkippedRow,
}) => {
  const { t } = useTranslation();

  const uploadColumnConflictsGrouped = useMemo(
    () => groupBy(uploadResult?.columnConflicts || [], "errorType"),
    [uploadResult?.columnConflicts],
  );

  const uploadRowConflictsGrouped = useMemo(
    () => groupBy(uploadResult?.rowConflicts || [], "errorType"),
    [uploadResult?.rowConflicts],
  );

  const hasColumnConflicts = useMemo(() => {
    return Object.keys(uploadColumnConflictsGrouped).length > 0;
  }, [uploadColumnConflictsGrouped]);

  const hasRowConflicts = useMemo(() => {
    return Object.keys(uploadRowConflictsGrouped).length > 0;
  }, [uploadRowConflictsGrouped]);

  return (
    <DrawerFormWrapperStyled>
      <GroupTitleTemplate name="preview" formLabel={t("general.PREVIEW")} />

      <PreviewDrawerLineStyled $numberColor={rydeColors.purpleTitle}>
        <span>{t("genericUpload.rowsToCreate")}</span>
        <span>{statistics.rowsToCreate}</span>
      </PreviewDrawerLineStyled>

      <PreviewDrawerLineStyled $numberColor={blueMui._600}>
        <span>{t("genericUpload.rowsToUpdate")}</span>
        <span>{statistics.rowsToUpdate}</span>
      </PreviewDrawerLineStyled>

      <PreviewDrawerLineStyled $numberColor={rydeColors.green}>
        <span>{t("genericUpload.totalRows")}</span>
        <span>{statistics.totalRows}</span>
      </PreviewDrawerLineStyled>

      {skippedDataIndexes.length > 0 && (
        <PreviewDrawerLineStyled $numberColor={blueMui._600}>
          <span>{t("genericUpload.phoneConflicts.skippedRows")}</span>
          <span>{skippedDataIndexes.length}</span>
        </PreviewDrawerLineStyled>
      )}

      {(phoneConflicts.length > 0 || isExistingPhoneCheckFailed) && (
        <GenericUploadPhoneConflicts
          conflicts={phoneConflicts}
          skippedDataIndexes={skippedDataIndexes}
          unresolvedRowsCount={unresolvedPhoneConflictRowsCount}
          isExistingCheckFailed={isExistingPhoneCheckFailed}
          onToggleSkippedRow={onToggleSkippedRow}
        />
      )}

      {hasColumnConflicts && (
        <GenericUploadColumnConflicts
          conflicts={uploadColumnConflictsGrouped}
        />
      )}

      {hasRowConflicts && (
        <GenericUploadRowConflicts
          conflicts={uploadRowConflictsGrouped}
        ></GenericUploadRowConflicts>
      )}
    </DrawerFormWrapperStyled>
  );
};
